import { FC, useCallback, useState } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { usePrivilege } from '@mews-commander/core';
import { lt, useDateTimeLocalizationInfo, useTimezone } from '@mews/framework';
import { SidePanel } from '@mews/b2b-ui';
import { Loader, useEnterpriseNavigate } from '@mews-commander/ui';
import { Interval, WithServiceId } from '@mews-commander/types';
import { useRatesQuery } from '@mews-commander/legacy-entities/src/queries/rates';
import { getResource } from '@mews-commander/legacy-entities/src/ducks/resources';
import { getResourceCategory } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { useServiceInfo } from '@mews-commander/legacy-entities/src/utils/useServiceInfo';
import { getSortedAgeCategoriesByServiceId } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { NewReservationInfo } from '../types';
import { NewReservationScreenUrlParamsKeys } from '../../../schemas/urlParamsSchema';
import { NewReservationFormKeys, NewReservationFormValues } from '../schema';
import { useOverbookingMessage } from '../utils/useOverbookingMessage';
import { useResourceOccupancyStateQuery } from '../../../../resourcesNew/queries/useResourceOccupancyStateQuery';
import { useIsReservationExceededMaxLength } from '../../../../reservationModule/utils/useIsReservationExceededMaxLength';
import { dateAndTimeToUtc } from '../utils/dateTime';
import { showNRSLoader } from '../../../actions';
import { NewReservationPanelProps } from './NewReservationPanel';
import { NewReservationPanelFormWrapper } from './NewReservationPanelFormWrapper';

interface NewReservationPanelWrapperProps extends WithServiceId, Pick<NewReservationPanelProps, 'onClose'> {
    newReservationInfo: NewReservationInfo;
    onDatesChange?: (interval: Interval) => void;
}

export const NewReservationPanelWrapper: FC<NewReservationPanelWrapperProps> = ({
    newReservationInfo,
    serviceId,
    onClose,
    onDatesChange,
    ...others
}) => {
    const dispatch = useCommanderThunkDispatch();
    const navigate = useEnterpriseNavigate();
    const timezone = useTimezone();
    const enterprise = useCurrentEnterprise();
    const localizationInfo = useDateTimeLocalizationInfo();
    const canOverbook = usePrivilege('overbookServices');
    const isReservationExceededMaxLength = useIsReservationExceededMaxLength(serviceId);

    const ratesQuery = useRatesQuery(enterprise.id, serviceId);
    const { timeUnitPeriod, bookablePeriod, isDayUse } = useServiceInfo(serviceId);

    const [selectedInterval, setSelectedInterval] = useState<Interval>({
        startUtc: newReservationInfo.startUtc,
        endUtc: newReservationInfo.endUtc,
    });

    const [reservationLengthError, setReservationLengthError] = useState<string | null>(null);

    const resource = useCommanderSelector((state) =>
        getResource(state, { id: newReservationInfo.resourceId, enterpriseId: enterprise.id })
    );

    const resourceCategory = useCommanderSelector((state) =>
        getResourceCategory(state, { id: newReservationInfo.resourceCategoryId, serviceId })
    );

    const ageCategories = useCommanderSelector((state) =>
        getSortedAgeCategoriesByServiceId(state, { serviceId }).filter((c) => !c.isDeleted)
    );

    const { overbookingMessage, loading } = useOverbookingMessage(
        serviceId,
        newReservationInfo.startUtc,
        newReservationInfo.endUtc,
        newReservationInfo.resourceCategoryId
    );

    const resourceOccupancyStateQuery = useResourceOccupancyStateQuery({
        serviceId,
        ...selectedInterval,
    });

    const onSubmit = useCallback(
        (values: NewReservationFormValues) => {
            const startUtc = dateAndTimeToUtc(
                values[NewReservationFormKeys.ArrivalDate],
                values[NewReservationFormKeys.ArrivalHours],
                localizationInfo,
                timezone
            )?.value;

            const endUtc = dateAndTimeToUtc(
                values[NewReservationFormKeys.DepartureDate],
                values[NewReservationFormKeys.DepartureHours],
                localizationInfo,
                timezone
            )?.value;
            // Date has been validated by form before submission, so dateAndTimeToUtc will not return null.
            if (!startUtc || !endUtc) {
                return;
            }
            dispatch(showNRSLoader());
            void navigate({
                to: 'Reservation/Create',
                search: {
                    [NewReservationScreenUrlParamsKeys.ServiceId]: serviceId,
                    [NewReservationScreenUrlParamsKeys.ResourceId]: newReservationInfo.resourceId,
                    [NewReservationScreenUrlParamsKeys.ResourceCategoryId]: newReservationInfo.resourceCategoryId,
                    [NewReservationScreenUrlParamsKeys.RateId]: values[NewReservationFormKeys.Rate],
                    [NewReservationScreenUrlParamsKeys.OccupancyData]: values[NewReservationFormKeys.AgeCategories],
                    [NewReservationScreenUrlParamsKeys.StartUtc]: startUtc,
                    [NewReservationScreenUrlParamsKeys.EndUtc]: endUtc,
                },
            });
        },
        [navigate, dispatch, serviceId, newReservationInfo, localizationInfo, timezone]
    );

    const onSelectedIntervalChanged: NewReservationPanelProps['onDatesChange'] = useCallback(
        ({
            arrival,
            departure,
        }: {
            arrival: { date: string; time: string };
            departure: { date: string; time: string };
        }) => {
            const startUtc = dateAndTimeToUtc(arrival.date, arrival.time, localizationInfo, timezone);
            const endUtc = dateAndTimeToUtc(departure.date, departure.time, localizationInfo, timezone);
            if (!startUtc || !endUtc) {
                return;
            }
            const interval = { startUtc: startUtc.value, endUtc: endUtc.value };

            const exceededError = isReservationExceededMaxLength(interval);

            if (exceededError) {
                setReservationLengthError(exceededError);
            }

            setSelectedInterval(interval);
            onDatesChange?.(interval);
        },
        [isReservationExceededMaxLength, onDatesChange, localizationInfo, timezone]
    );

    if (ratesQuery.isFetching || !ratesQuery.data?.defaultRate || !resourceCategory || !resource) {
        return (
            <SidePanel
                opened
                title={lt('NewReservation')}
                width="small"
                onClose={onClose}
                headerActions={[]}
                content={<Loader />}
            />
        );
    }

    return (
        <NewReservationPanelFormWrapper
            {...others}
            serviceId={serviceId}
            isDayUse={!!isDayUse}
            loading={loading || resourceOccupancyStateQuery.isFetching}
            overbookingMessage={overbookingMessage}
            reservationLengthError={reservationLengthError}
            canOverbook={canOverbook}
            timeUnitPeriod={timeUnitPeriod}
            bookablePeriod={bookablePeriod}
            resource={resource}
            resourceCategory={resourceCategory}
            startUtc={newReservationInfo.startUtc}
            endUtc={newReservationInfo.endUtc}
            serviceDefaultRate={ratesQuery.data?.defaultRate}
            ratesById={ratesQuery.data.ratesById}
            ageCategories={ageCategories}
            resourceOccupancyStates={
                resourceOccupancyStateQuery.data?.aggregatedData.occupancyStatesByResourceId[resource.id]
            }
            onSubmit={onSubmit}
            onClose={onClose}
            onDatesChange={onSelectedIntervalChanged}
        />
    );
};
